import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { scrollIntoView } from "@theme/scrolling";

const INDEX_RECONCILIATION_QUIET_MS = 240;

/**
 * @typedef {Object} RelatedTeasRefs
 * @property {HTMLUListElement} track
 * @property {HTMLLIElement[]} items
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 */

/**
 * A progressively enhanced mobile rail. The DOM remains an ordered list and native horizontal
 * scrolling remains available without JavaScript. JavaScript only supplies non-gesture controls,
 * focus reveal, and truthful boundary states; it never auto-advances or announces position.
 *
 * @extends {Component<RelatedTeasRefs>}
 */
class RelatedTeasComponent extends Component {
  requiredRefs = ["track", "previous", "next"];

  #eventController = new AbortController();

  /** @type {HTMLUListElement | null} */
  #boundTrack = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #frame = null;

  /** @type {number | null} */
  #scrollSettleFrame = null;

  #lastScrollPosition = 0;

  #lastScrollActivityTime = 0;

  #lastControlIntentTime = 0;

  #activeIndex = 0;

  #handleNativeScroll = () => {
    this.#queueBoundaryUpdate();
    this.#queueIndexReconciliation();
  };

  /** @param {FocusEvent} event */
  #handleFocus = event => {
    const target = event.target;
    const item = target instanceof Element ? target.closest(".related-teas__item") : null;
    if (!(item instanceof HTMLLIElement) || !this.refs.track.contains(item)) {
      return;
    }

    if (!this.#isFullyVisible(item)) {
      scrollIntoView(item, { behavior: "smooth", block: "nearest", inline: "nearest" });
    }
    this.#activeIndex = Math.max(0, (this.refs.items ?? []).indexOf(item));
    this.#queueBoundaryUpdate();
  };

  /** @param {import("@theme/events").VariantUpdateEvent} event */
  #handleVariantUpdate = event => {
    if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const resource = /** @type {{ option1?: string; options?: string[] } | null} */ (
      /** @type {unknown} */ (event.detail.resource)
    );
    const format = resource?.option1 ?? resource?.options?.[0];
    if (!format) {
      return;
    }

    for (const link of this.querySelectorAll(".related-teas__card")) {
      this.#updateVariantLink(link, format);
    }
  };

  /**
   * @param {Element} link
   * @param {string} format
   */
  #updateVariantLink(link, format) {
    if (!(link instanceof HTMLAnchorElement)) {
      return;
    }

    const productUrl = link.dataset.relatedProductUrl;
    const variantMap = link.dataset.relatedVariantMap;
    if (!productUrl || !variantMap) {
      return;
    }

    try {
      const variants = /** @type {Record<string, string | number>} */ (JSON.parse(variantMap));
      const url = new URL(productUrl, globalThis.location.origin);
      const variantId = variants[format];

      if (variantId) {
        url.searchParams.set("variant", `${variantId}`);
      } else {
        url.searchParams.delete("variant");
      }

      link.href = url.toString();
    } catch (error) {
      console.error("Unable to update a Related Teas variant link.", error);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#eventController = new AbortController();
    this.addEventListener("focusin", this.#handleFocus);
    document.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.#handleVariantUpdate), {
      signal: this.#eventController.signal,
    });
    this.#bindTrack();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindTrack();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventController.abort();
    this.removeEventListener("focusin", this.#handleFocus);
    this.#boundTrack?.removeEventListener("scroll", this.#handleNativeScroll);
    this.#boundTrack = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
    this.#cancelIndexReconciliation();
  }

  /** Move to the previous item in DOM order. */
  previous() {
    this.#move(-1);
  }

  /** Move to the next item in DOM order. */
  next() {
    this.#move(1);
  }

  #bindTrack() {
    const track = this.refs.track;
    if (this.#boundTrack !== track) {
      this.#boundTrack?.removeEventListener("scroll", this.#handleNativeScroll);
      this.#cancelIndexReconciliation();
      this.#boundTrack = track;
      track.addEventListener("scroll", this.#handleNativeScroll, { passive: true });
    }

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#queueBoundaryUpdate());
    this.#resizeObserver.observe(track);
    this.#activeIndex = this.#mostVisibleIndex();
    this.#queueBoundaryUpdate();
  }

  /** @param {-1 | 1} offset */
  #move(offset) {
    const items = this.refs.items ?? [];
    if (items.length === 0) {
      return;
    }

    const currentIndex = this.#activeIndex;
    const targetIndex = Math.max(0, Math.min(items.length - 1, currentIndex + offset));
    const target = items[targetIndex];
    if (!target || targetIndex === currentIndex) {
      this.#updateBoundaries();
      return;
    }

    this.#activeIndex = targetIndex;
    this.#lastControlIntentTime = performance.now();
    scrollIntoView(target, { behavior: "smooth", block: "nearest", inline: "start" });
    this.refs.previous.disabled = targetIndex === 0;
    this.refs.next.disabled = targetIndex === items.length - 1;
  }

  #queueBoundaryUpdate() {
    if (this.#frame !== null) {
      return;
    }
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#updateBoundaries();
    });
  }

  #queueIndexReconciliation() {
    this.#lastScrollPosition = this.refs.track.scrollLeft;
    this.#lastScrollActivityTime = performance.now();
    if (this.#scrollSettleFrame !== null) {
      return;
    }

    /** @param {number} timestamp */
    const reconcileWhenSettled = timestamp => {
      const position = this.refs.track.scrollLeft;
      if (Math.abs(position - this.#lastScrollPosition) > 0.5) {
        this.#lastScrollPosition = position;
        this.#lastScrollActivityTime = timestamp;
      }

      const quietSince = Math.max(this.#lastScrollActivityTime, this.#lastControlIntentTime);
      if (timestamp - quietSince >= INDEX_RECONCILIATION_QUIET_MS) {
        this.#scrollSettleFrame = null;
        this.#activeIndex = this.#mostVisibleIndex();
        this.#updateBoundaries();
        return;
      }

      this.#scrollSettleFrame = requestAnimationFrame(reconcileWhenSettled);
    };

    this.#scrollSettleFrame = requestAnimationFrame(reconcileWhenSettled);
  }

  #cancelIndexReconciliation() {
    if (this.#scrollSettleFrame !== null) {
      cancelAnimationFrame(this.#scrollSettleFrame);
      this.#scrollSettleFrame = null;
    }
    this.#lastScrollActivityTime = 0;
  }

  #updateBoundaries() {
    const items = this.refs.items ?? [];
    const first = items[0];
    const last = items.at(-1);
    const hasOverflow = this.refs.track.scrollWidth > this.refs.track.clientWidth + 1;

    this.refs.previous.disabled = !hasOverflow || !first || this.#isFullyVisible(first);
    this.refs.next.disabled = !hasOverflow || !last || this.#isFullyVisible(last);
  }

  /** @returns {number} */
  #mostVisibleIndex() {
    const trackRect = this.refs.track.getBoundingClientRect();
    let bestIndex = 0;
    let bestRatio = -1;

    for (const [index, item] of (this.refs.items ?? []).entries()) {
      const rect = item.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left));
      const ratio = rect.width > 0 ? visibleWidth / rect.width : 0;
      if (ratio > bestRatio) {
        bestIndex = index;
        bestRatio = ratio;
      }
    }

    return bestIndex;
  }

  /** @param {Element} item */
  #isFullyVisible(item) {
    const trackRect = this.refs.track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const tolerance = 1;
    return itemRect.left >= trackRect.left - tolerance && itemRect.right <= trackRect.right + tolerance;
  }
}

if (!customElements.get("related-teas-component")) {
  customElements.define("related-teas-component", RelatedTeasComponent);
}

export { RelatedTeasComponent };
